import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ReadAloudButton } from "@/components/ReadAloudButton";
import { X, ChevronRight, Eye, Layers, BookOpen } from "lucide-react";

export function LessonFlashcards({ lesson, onClose, accent = "#81B29A" }) {
  const cards = useMemo(() => {
    const vocab = (lesson.vocabulary || []).map((v) => ({ label: "Vocabulary", front: v.term, back: v.definition }));
    const facts = (lesson.key_facts || []).map((f, i) => ({ label: "Key Fact", front: `Do you remember key fact #${i + 1}?`, back: f }));
    return [...vocab, ...facts];
  }, [lesson]);

  const [i, setI] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const next = () => { setRevealed(false); setI((x) => (x + 1) % cards.length); };

  if (cards.length === 0) return null;
  const c = cards[i];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div data-testid="lesson-flashcard-modal" initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
        onClick={(e) => e.stopPropagation()} className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-3 text-white">
          <span className="flex items-center gap-2 text-sm"><Layers size={15} /> Card {i + 1} / {cards.length}</span>
          <button data-testid="lesson-flashcard-close" onClick={onClose} className="text-white/80 hover:text-white text-sm">Close</button>
        </div>
        <div data-testid="lesson-flashcard" onClick={() => setRevealed((r) => !r)}
          className="min-h-[280px] rounded-3xl bg-white p-8 flex flex-col items-center justify-center text-center cursor-pointer select-none grain relative overflow-hidden">
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: accent }}>{c.label}</span>
          <p className="font-head text-2xl font-bold text-[#3D405B] mt-3">{c.front}</p>
          {revealed ? (
            <p className="text-[#3D405B] leading-relaxed mt-4">{c.back}</p>
          ) : (
            <p className="text-[#6B7280] mt-4 flex items-center gap-2"><Eye size={16} /> Tap to reveal</p>
          )}
        </div>
        <div className="flex items-center justify-center gap-3 mt-4">
          <ReadAloudButton testid="lesson-flashcard-read" label="Hear it" text={revealed ? `${c.front}. ${c.back}` : c.front} tone="#fff" />
          <button data-testid="lesson-flashcard-next" onClick={next}
            className="flex items-center gap-2 rounded-full text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105" style={{ background: accent }}>
            Next card <ChevronRight size={16} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function LessonFlashcardsButton({ lesson, accent = "#81B29A" }) {
  const [open, setOpen] = useState(false);
  const count = (lesson.vocabulary || []).length + (lesson.key_facts || []).length;
  if (count === 0) return null;
  return (
    <>
      <button data-testid="lesson-flashcards-btn" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors hover:text-white"
        style={{ borderColor: accent, color: open ? "#fff" : accent }}>
        <BookOpen size={15} /> Study flashcards
      </button>
      {open && <LessonFlashcards lesson={lesson} accent={accent} onClose={() => setOpen(false)} />}
    </>
  );
}
